import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight, Download, PackageSearch, Info, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { CATEGORY_LABEL, baseModel, type EquipCategory } from "@/lib/equipment";
import { useEquipmentInstalls, type EquipInstallRow } from "./api";
import { useModelClassifications, modelKey, MIN_CONFIDENCE, type ClassifyKey } from "./classification";

// ============================================================================
// REPORT-01 — Equipment installs. The demand-history report: how many units of
// each kind we installed over a period, sliceable by make / model / zone /
// month. Front half of the seasonal inventory forecast (REPORT-04).
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

// ---- date helpers (local, ISO YYYY-MM-DD) ----
function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayISO(): string {
  return iso(new Date());
}
// Most recent COMPLETED summer (Jun 1 – Aug 31). Before September, "last
// summer" is last year's; from September on, it's this year's.
function lastSummer(): { from: string; to: string; label: string } {
  const now = new Date();
  const yr = now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${yr}-06-01`, to: `${yr}-08-31`, label: `Summer ${yr}` };
}
function yearsAgoISO(n: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return iso(d);
}

type PresetKey = "last-summer" | "this-year" | "last-12mo" | "last-3yr" | "all" | "custom";

function presetRange(key: PresetKey): { from: string; to: string } {
  const today = todayISO();
  switch (key) {
    case "last-summer": {
      const s = lastSummer();
      return { from: s.from, to: s.to };
    }
    case "this-year":
      return { from: `${new Date().getFullYear()}-01-01`, to: today };
    case "last-12mo":
      return { from: yearsAgoISO(1), to: today };
    case "last-3yr":
      return { from: yearsAgoISO(3), to: today };
    case "all":
      return { from: "1990-01-01", to: today };
    default:
      return { from: lastSummer().from, to: lastSummer().to };
  }
}

const PRESETS: [PresetKey, string][] = [
  ["last-summer", "Last summer"],
  ["this-year", "This year"],
  ["last-12mo", "Last 12 months"],
  ["last-3yr", "Last 3 years"],
  ["all", "All time"],
  ["custom", "Custom range"],
];

type TierKey = "all" | "system" | "auxiliary" | "consumable" | "material";
const TIERS: [TierKey, string][] = [
  ["all", "All equipment"],
  ["system", "Systems only"],
  ["auxiliary", "Aux & IAQ"],
  ["consumable", "Consumables"],
  ["material", "Install materials"],
];

type GroupKey = "category" | "mfg" | "model" | "zone" | "month";
const GROUPS: [GroupKey, string][] = [
  ["category", "Category"],
  ["mfg", "Make"],
  ["model", "Model"],
  ["zone", "Zone (city)"],
  ["month", "Month"],
];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function groupValue(row: EquipInstallRow, by: GroupKey): string {
  switch (by) {
    case "category":
      return row.categoryLabel;
    case "mfg":
      return (row.mfg ?? "").trim().toUpperCase() || "(unknown make)";
    case "model":
      // Collapse the factory dash/revision suffix so -09 and -05 of the same
      // unit count together (revision matters for parts, not unit demand).
      return baseModel(row.model) || "(unknown model)";
    case "zone":
      return (row.city ?? "").trim() || "(unknown zone)";
    case "month":
      return row.install_date.slice(0, 7); // YYYY-MM
  }
}

interface GroupRow {
  key: string;
  label: string;
  count: number;
}

function StyledSelect({ value, onChange, options }: {
  value: string; onChange: (v: string) => void; options: [string, string][];
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
      >
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function EquipmentInstallsReport() {
  const [preset, setPreset] = useState<PresetKey>("last-summer");
  const [from, setFrom] = useState<string>(() => presetRange("last-summer").from);
  const [to, setTo] = useState<string>(() => presetRange("last-summer").to);
  const [tier, setTier] = useState<TierKey>("all");
  const [groupBy, setGroupBy] = useState<GroupKey>("category");
  // Drill-down: when set (Category view only), show this category's models
  // instead of the all-category roll-up. The breadcrumb clears it.
  const [drillCat, setDrillCat] = useState<EquipCategory | null>(null);

  const { data, isLoading, isError } = useEquipmentInstalls(from, to);

  // Leaving Category view abandons any drill-down.
  function changeGroupBy(g: GroupKey) {
    setGroupBy(g);
    if (g !== "category") setDrillCat(null);
  }
  const drilling = groupBy === "category" && drillCat !== null;

  function choosePreset(key: PresetKey) {
    setPreset(key);
    if (key !== "custom") {
      const r = presetRange(key);
      setFrom(r.from);
      setTo(r.to);
    }
  }

  // The models the deterministic rules left as "other" — these are what the AI
  // classifier earns its keep on. Only relevant when grouping by Category;
  // collected with install frequency so the AI prioritizes the high-volume tail.
  const otherKeys = useMemo<ClassifyKey[]>(() => {
    if (groupBy !== "category") return [];
    const counts = new Map<string, ClassifyKey>();
    for (const r of data?.rows ?? []) {
      if (r.category !== "other") continue;
      const k = modelKey(r.mfg, r.model);
      const existing = counts.get(k);
      if (existing) existing.count += 1;
      else counts.set(k, { mfg: r.mfg ?? "", model: r.model ?? "", count: 1 });
    }
    return Array.from(counts.values());
  }, [data, groupBy]);

  const { data: aiMap, isFetching: aiLoading } = useModelClassifications(otherKeys);

  // Resolve a row's effective category: rules first; for "other", fall back to
  // an AI label at/above the confidence gate. Below the gate stays "other".
  function effectiveCategory(r: EquipInstallRow): EquipCategory {
    if (r.category !== "other") return r.category;
    const hit = aiMap?.get(modelKey(r.mfg, r.model));
    if (hit && hit.category !== "other" && (hit.confidence ?? 0) >= MIN_CONFIDENCE) {
      return hit.category;
    }
    return "other";
  }

  // Tier-filter, then group + count. In Category view we group by the row's
  // EFFECTIVE category (rules + AI). When drilled into one category, we instead
  // filter to that category and group by base-model — so "84 Furnaces" opens
  // into the actual furnace models behind it.
  const { groups, total, tierTotal, aiApplied } = useMemo(() => {
    const all = data?.rows ?? [];
    const tierTotal = all.length;
    const filtered = tier === "all" ? all : all.filter((r) => r.tier === tier);
    const map = new Map<string, number>();
    // Preserve a representative full model per base-model key for display.
    const repModel = new Map<string, string>();
    let aiApplied = 0;
    for (const r of filtered) {
      let k: string;
      if (groupBy === "category") {
        const cat = effectiveCategory(r);
        if (r.category === "other" && cat !== "other") aiApplied += 1;
        if (drillCat) {
          if (cat !== drillCat) continue; // only this category's rows
          const base = baseModel(r.model) || "(unknown model)";
          k = `${(r.mfg ?? "").trim().toUpperCase()} ${base}`.trim();
          if (!repModel.has(k)) repModel.set(k, base);
        } else {
          k = CATEGORY_LABEL[cat];
        }
      } else {
        k = groupValue(r, groupBy);
      }
      map.set(k, (map.get(k) ?? 0) + 1);
    }
    let groups: GroupRow[] = Array.from(map.entries()).map(([key, count]) => ({
      key,
      label: groupBy === "month" ? monthLabel(key) : key,
      count,
    }));
    // Month reads chronologically; everything else is ranked by volume.
    groups =
      groupBy === "month"
        ? groups.sort((a, b) => a.key.localeCompare(b.key))
        : groups.sort((a, b) => b.count - a.count);
    return { groups, total: groups.reduce((s, g) => s + g.count, 0), tierTotal, aiApplied };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tier, groupBy, drillCat, aiMap]);

  // Map a category label back to its key, so a click on a category row knows
  // which EquipCategory to drill into.
  const labelToCat = useMemo(() => {
    const m = new Map<string, EquipCategory>();
    (Object.keys(CATEGORY_LABEL) as EquipCategory[]).forEach((c) => m.set(CATEGORY_LABEL[c], c));
    return m;
  }, []);

  // Is a given group row drillable? Only category rows (not "Equipment"/other or
  // material/consumable buckets, which have nothing meaningful to break down).
  function drillTarget(label: string): EquipCategory | null {
    if (groupBy !== "category" || drilling) return null;
    const cat = labelToCat.get(label);
    if (!cat || cat === "other" || cat === "material" || cat === "consumable") return null;
    return cat;
  }

  const max = groups.reduce((m, g) => Math.max(m, g.count), 0);
  const TOP = 15;
  const chartGroups = groupBy === "month" ? groups : groups.slice(0, TOP);

  function exportCsv() {
    const rangeLabel = `${from} to ${to}`;
    const tierLabel = TIERS.find(([k]) => k === tier)?.[1] ?? "All equipment";
    const groupLabel = GROUPS.find(([k]) => k === groupBy)?.[1] ?? groupBy;
    const lines: string[] = [];
    lines.push(`Sedgwick Heating — Equipment installs`);
    lines.push(`Date range,${rangeLabel}`);
    lines.push(`Type,${tierLabel}`);
    lines.push(`Grouped by,${groupLabel}`);
    lines.push(`Total installs,${total}`);
    if (data?.truncated) lines.push(`Note,Results capped — totals are a floor; narrow the range`);
    lines.push("");
    lines.push(`${groupLabel},Installs,Percent`);
    for (const g of groups) {
      const pct = total > 0 ? ((g.count / total) * 100).toFixed(1) : "0.0";
      const safe = /[",\n]/.test(g.label) ? `"${g.label.replace(/"/g, '""')}"` : g.label;
      lines.push(`${safe},${g.count},${pct}%`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `equipment-installs_${groupBy}_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      {/* Controls */}
      <Card className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Date range">
            <StyledSelect value={preset} onChange={(v) => choosePreset(v as PresetKey)} options={PRESETS} />
          </Field>
          <Field label="From">
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
              className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </Field>
          <Field label="To">
            <input
              type="date"
              value={to}
              min={from}
              max={todayISO()}
              onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
              className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
          </Field>
          <Field label="Type">
            <StyledSelect value={tier} onChange={(v) => setTier(v as TierKey)} options={TIERS} />
          </Field>
          <Field label="Group by">
            <StyledSelect value={groupBy} onChange={(v) => changeGroupBy(v as GroupKey)} options={GROUPS} />
          </Field>
          <div className="ml-auto">
            <Button variant="secondary" size="sm" onClick={exportCsv} disabled={total === 0}>
              <Download className="h-4 w-4" /> Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary line */}
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-slate-600">
          {!isLoading && !isError && (
            <>
              <span className="text-2xl font-bold tabular-nums text-slate-900">{total.toLocaleString()}</span>
              <span className="ml-2">installs · {groups.length.toLocaleString()} {GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()} groups</span>
              {tier !== "all" && tierTotal > 0 && (
                <span className="text-slate-400"> · {Math.round((total / tierTotal) * 100)}% of all equipment in range</span>
              )}
              <InfoTip className="ml-2" label="How this report is figured">
                Counts equipment with a known install date; about half of older records carry a placeholder date from an old import and are left out of dated ranges. Categories match the Equipment tab on the customer page. Click a category to see its models.
              </InfoTip>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {groupBy === "category" && aiLoading && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600">
              <Sparkles className="h-3.5 w-3.5 animate-pulse" /> Classifying models with AI…
            </span>
          )}
          {groupBy === "category" && !aiLoading && aiApplied > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600" title="Models the built-in rules couldn't place were identified by AI (Gemini) and cached. Low-confidence ones stay under Equipment.">
              <Sparkles className="h-3.5 w-3.5" /> {aiApplied.toLocaleString()} AI-classified
            </span>
          )}
          {data?.truncated && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
              <Info className="h-3.5 w-3.5" /> Results capped — narrow the range for exact totals
            </span>
          )}
        </div>
      </div>

      {isError ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-slate-600">Couldn't load equipment installs. Check the connection and try again.</p>
        </Card>
      ) : isLoading ? (
        <ChartSkeleton />
      ) : total === 0 ? (
        <Card className="p-10 text-center">
          <PackageSearch className="mx-auto h-10 w-10 text-slate-300" />
          <h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No installs in this range</h3>
          <p className="mt-1 text-xs text-slate-500">Try a wider date range or a different equipment type.</p>
        </Card>
      ) : (
        <>
          {/* Drill breadcrumb */}
          {drilling && drillCat && (
            <button
              type="button"
              onClick={() => setDrillCat(null)}
              className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 transition-colors hover:text-brand-800"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> All categories
              <span className="text-slate-400">/</span>
              <span className="text-slate-900">{CATEGORY_LABEL[drillCat]}</span>
            </button>
          )}

          {/* Bar chart */}
          <Card className="mb-4 p-5">
            <h3 className="mb-4 text-[15px] font-bold tracking-tight text-slate-900">
              {drilling && drillCat
                ? `${CATEGORY_LABEL[drillCat]} models`
                : `Installs by ${GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()}`}
              {chartGroups.length < groups.length && <span className="font-normal text-slate-400"> · top {TOP}</span>}
            </h3>
            <div className="space-y-2.5">
              {chartGroups.map((g, i) => {
                const target = drillTarget(g.label);
                return (
                <div
                  key={g.key}
                  className={cn("flex items-center gap-3", target && "cursor-pointer group/row")}
                  onClick={target ? () => setDrillCat(target) : undefined}
                  role={target ? "button" : undefined}
                  tabIndex={target ? 0 : undefined}
                  onKeyDown={target ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrillCat(target); } } : undefined}
                >
                  <div className={cn("w-40 flex-shrink-0 truncate text-right text-xs font-medium text-slate-600", target && "group-hover/row:text-brand-700")} title={g.label}>
                    {g.label}
                  </div>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${max > 0 ? (g.count / max) * 100 : 0}%` }}
                      transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.02, 0.25) }}
                      className="h-full rounded-md bg-brand-500"
                    />
                  </div>
                  <div className="flex w-20 flex-shrink-0 items-center justify-end gap-1 text-right text-xs tabular-nums">
                    <span className="font-semibold text-slate-900">{g.count.toLocaleString()}</span>
                    <span className="text-slate-400">{((g.count / total) * 100).toFixed(0)}%</span>
                    {target && <ChevronRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover/row:translate-x-0.5 group-hover/row:text-brand-500" />}
                  </div>
                </div>
                );
              })}
            </div>
          </Card>

          {/* Table */}
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">{drilling ? "Model" : GROUPS.find(([k]) => k === groupBy)?.[1]}</th>
                  <th className="px-4 py-2.5 text-right">Installs</th>
                  <th className="px-4 py-2.5 text-right">% of total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => {
                  const target = drillTarget(g.label);
                  return (
                  <tr
                    key={g.key}
                    className={cn("transition-colors hover:bg-brand-50/40", target && "cursor-pointer")}
                    onClick={target ? () => setDrillCat(target) : undefined}
                    role={target ? "button" : undefined}
                    tabIndex={target ? 0 : undefined}
                    onKeyDown={target ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrillCat(target); } } : undefined}
                  >
                    <td className="px-4 py-2.5 font-medium text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        {g.label}
                        {target && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{g.count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{((g.count / total) * 100).toFixed(1)}%</td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                  <td className="px-4 py-2.5 text-slate-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{total.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">100%</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function ChartSkeleton() {
  return (
    <Card className="p-5">
      <div className="mb-4 h-4 w-48 animate-pulse rounded bg-slate-100" />
      <div className="space-y-2.5">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="h-4 w-40 flex-shrink-0 animate-pulse rounded bg-slate-100" />
            <div className="h-6 flex-1 animate-pulse rounded-md bg-slate-100" style={{ opacity: 1 - i * 0.08 }} />
            <div className="h-4 w-20 flex-shrink-0 animate-pulse rounded bg-slate-100" />
          </div>
        ))}
      </div>
    </Card>
  );
}
